import React, { useEffect } from 'react';
import FormField from '../shared/FormField';
import CountrySelect, { getCountryName } from '../shared/CountrySelect';
import formDataStorage from '../../utils/formDataStorage';
import { 
  Users, Globe, Building2, Award, Shield, AlertTriangle, 
  Plus, Trash2, Languages, MapPin 
} from 'lucide-react';

// 工作教育3 - 特殊信息和背景调查表单
const WorkEducation3Form = ({ data, onChange }) => {

  // 获取出生国家信息
  const personal1Data = formDataStorage.loadFormData('personal_1');
  const birthCountry = personal1Data?.date_and_place_of_birth?.country;

  // 确保至少有一个语言选项，默认包含中文
  useEffect(() => {
    const currentLanguages = data.languages || [];
    if (currentLanguages.length === 0) {
      onChange('languages', [{ language_name: 'CHINESE' }]);
    }
  }, [data.languages, onChange]);

  // 月份映射
  const MONTHS = [
    { value: '1', label: '01月' },
    { value: '2', label: '02月' },
    { value: '3', label: '03月' },
    { value: '4', label: '04月' },
    { value: '5', label: '05月' },
    { value: '6', label: '06月' },
    { value: '7', label: '07月' },
    { value: '8', label: '08月' },
    { value: '9', label: '09月' },
    { value: '10', label: '10月' },
    { value: '11', label: '11月' },
    { value: '12', label: '12月' }
  ];

  // 语言选项
  const LANGUAGE_OPTIONS = [
    { value: 'CHINESE', label: '中文' },
    { value: 'ENGLISH', label: '英语' },
    { value: 'SPANISH', label: '西班牙语' },
    { value: 'FRENCH', label: '法语' },
    { value: 'GERMAN', label: '德语' },
    { value: 'JAPANESE', label: '日语' },
    { value: 'KOREAN', label: '韩语' },
    { value: 'ARABIC', label: '阿拉伯语' },
    { value: 'RUSSIAN', label: '俄语' },
    { value: 'PORTUGUESE', label: '葡萄牙语' },
    { value: 'ITALIAN', label: '意大利语' },
    { value: 'HINDI', label: '印地语' },
    { value: 'OTHER', label: '其他' }
  ];

  // 获取指定年月的天数
  const getDaysInMonth = (year, month) => {
    if (!year || !month) return 31;
    return new Date(year, month, 0).getDate();
  };

  // 生成日期选项
  const generateDayOptions = (year, month) => {
    const maxDays = getDaysInMonth(year, month);
    return Array.from({ length: maxDays }, (_, i) => {
      const day = String(i + 1).padStart(2, '0');
      return { value: day, label: `${day}日` };
    });
  };

  // 添加语言
  const addLanguage = () => {
    const currentLanguages = data.languages || [];
    onChange('languages', [...currentLanguages, { language_name: '' }]);
  };

  // 删除语言
  const removeLanguage = (index) => {
    const currentLanguages = data.languages || [];
    // 确保至少保留一个语言选项
    if (currentLanguages.length > 1) {
      const newLanguages = currentLanguages.filter((_, i) => i !== index);
      onChange('languages', newLanguages);
    }
  };

  // 更新语言
  const updateLanguage = (index, value) => {
    const currentLanguages = data.languages || [];
    const newLanguages = [...currentLanguages];
    newLanguages[index] = { language_name: value };
    onChange('languages', newLanguages);
  };

  // 添加访问国家
  const addCountryVisited = () => {
    const currentCountries = data.countries_visited || [];
    onChange('countries_visited', [...currentCountries, { country_code: '', country_name: '' }]);
  };

  // 删除访问国家
  const removeCountryVisited = (index) => {
    const currentCountries = data.countries_visited || [];
    const newCountries = currentCountries.filter((_, i) => i !== index);
    onChange('countries_visited', newCountries);
  };

  // 更新访问国家
  const updateCountryVisited = (index, countryCode) => {
    const currentCountries = data.countries_visited || [];
    const newCountries = [...currentCountries];
    // 获取国家名称
    const countryName = getCountryName(countryCode);
    newCountries[index] = { 
      country_code: countryCode, 
      country_name: countryName 
    };
    onChange('countries_visited', newCountries);
  };

  // 添加组织
  const addOrganization = () => {
    const currentOrgs = data.organizations || [];
    onChange('organizations', [...currentOrgs, { organization_name: '' }]);
  };

  // 删除组织
  const removeOrganization = (index) => {
    const currentOrgs = data.organizations || [];
    const newOrgs = currentOrgs.filter((_, i) => i !== index);
    onChange('organizations', newOrgs);
  };

  // 更新组织
  const updateOrganization = (index, value) => {
    const currentOrgs = data.organizations || [];
    const newOrgs = [...currentOrgs];
    newOrgs[index] = { organization_name: value };
    onChange('organizations', newOrgs);
  };

  // 添加军事服务
  const addMilitaryService = () => {
    const currentServices = data.military_services || [];
    const newService = {
      country_code: '',
      country_name: '',
      branch: '',
      rank: '',
      specialty: '',
      date_from: { day: '', month: '', year: '' },
      date_to: { day: '', month: '', year: '' }
    };
    onChange('military_services', [...currentServices, newService]);
  };

  // 删除军事服务
  const removeMilitaryService = (index) => {
    const currentServices = data.military_services || [];
    const newServices = currentServices.filter((_, i) => i !== index);
    onChange('military_services', newServices);
  };

  // 更新军事服务
  const updateMilitaryService = (index, field, value) => {
    const currentServices = data.military_services || [];
    const newServices = [...currentServices];
    if (field === 'country_code') {
      newServices[index] = {
        ...newServices[index],
        country_code: value,
        country_name: getCountryName(value)
      };
    } else {
      newServices[index] = {
        ...newServices[index],
        [field]: value
      };
    }
    onChange('military_services', newServices);
  };

  // 更新军事服务日期
  const updateMilitaryServiceDate = (index, dateType, field, value) => {
    const currentServices = data.military_services || [];
    const currentService = currentServices[index] || {};
    const currentDate = currentService[dateType] || {};
    const newDate = { ...currentDate, [field]: value };
    
    // 如果修改了年或月，需要检查日期是否有效
    if (field === 'year' || field === 'month') {
      const maxDays = getDaysInMonth(newDate.year, newDate.month);
      if (newDate.day && parseInt(newDate.day) > maxDays) {
        newDate.day = String(maxDays);
      }
    }
    
    const newService = { ...currentService, [dateType]: newDate };
    const newServices = [...currentServices];
    newServices[index] = newService;
    onChange('military_services', newServices);
  };

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-indigo-50 to-purple-50 p-6 rounded-lg">
        <h3 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
          <span className="w-8 h-8 bg-indigo-600 text-white rounded-full flex items-center justify-center mr-3 text-sm">🔍</span>
          特殊信息和背景调查
        </h3>

        {/* 部落/宗族信息 */}
        <div className="bg-white p-6 rounded-lg shadow-sm mb-6">
          <div className="mb-4">
            <h4 className="text-lg font-semibold text-gray-800 flex items-center mb-4">
              <Users className="w-5 h-5 mr-2 text-indigo-600" />
              您是否属于部落或宗族？
            </h4>
          </div>
          <div>

            <div className="flex gap-6">
              <label className="flex items-center cursor-pointer p-3 border-2 border-gray-200 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-all duration-200">
                <input
                  type="radio"
                  name="clan_tribe_ind"
                  value="false"
                  checked={data.clan_tribe_ind !== true}
                  onChange={(e) => onChange('clan_tribe_ind', false)}
                  className="mr-3 w-5 h-5 text-blue-600 border-gray-300 focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50"
                />
                <span className="text-base font-medium text-gray-700">❌ 否 - 我不属于部落或宗族</span>
              </label>

              <label className="flex items-center cursor-pointer p-3 border-2 border-gray-200 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-all duration-200">
                <input
                  type="radio"
                  name="clan_tribe_ind"
                  value="true"
                  checked={data.clan_tribe_ind === true}
                  onChange={(e) => onChange('clan_tribe_ind', true)}
                  className="mr-3 w-5 h-5 text-blue-600 border-gray-300 focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50"
                />
                <span className="text-base font-medium text-gray-700">✅ 是 - 我属于部落或宗族</span>
              </label>
            </div>
          </div>

          {data.clan_tribe_ind && (
            <div className="mt-4 space-y-3">
              {/* 强烈风险警告 */}
              <div className="bg-red-50 border-2 border-red-400 rounded-lg p-4 animate-pulse">
                <div className="flex items-start">
                  <AlertTriangle className="w-6 h-6 text-red-600 mt-0.5 mr-3 flex-shrink-0" />
                  <div className="text-sm text-red-800">
                    <p className="font-bold text-base mb-2">⚠️ 重要风险警告</p>
                    <p className="mb-2">
                      <span className="font-bold">选择"是"将大幅增加拒签风险！</span>
                      此选项可能导致：
                    </p>
                    <ul className="list-disc list-inside ml-2 space-y-1">
                      <li className="font-semibold">面签时遭到严格审查</li>
                      <li className="font-semibold">签证申请被拒绝</li>
                      <li className="font-semibold">可能面临永久拒签</li>
                    </ul>
                    <p className="mt-3 font-bold text-red-900 bg-red-100 p-2 rounded">
                      💡 强烈建议：如无必要，请仔细核实后选择"否"。如确实存在相关情况，请立即咨询专业移民律师！
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* 语言信息 */}
        <div className="bg-white p-6 rounded-lg shadow-sm mb-6">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-lg font-semibold text-gray-800 flex items-center">
              <Languages className="w-5 h-5 mr-2" />
              语言能力
            </h4>
            <button
              type="button"
              onClick={addLanguage}
              className="flex items-center px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all duration-200 text-sm font-medium shadow-sm hover:shadow-md"
            >
              <Plus className="w-4 h-4 mr-1" />
              添加语言
            </button>
          </div>

          {(data.languages || []).length === 0 && (
            <p className="text-gray-500 text-sm">请至少添加一种您能说的语言</p>
          )}

          <div className="space-y-3">
            {(data.languages || []).map((language, index) => (
              <div key={index} className="flex items-center gap-3">
                <select
                  value={language.language_name || ''}
                  onChange={(e) => updateLanguage(index, e.target.value)}
                  className="flex-1 h-10 px-3 py-2 pr-8 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gradient-to-r from-white to-blue-50 shadow-sm hover:shadow-md transition-all duration-200 appearance-none cursor-pointer font-medium text-sm"
                  required
                >
                  <option value="">请选择语言</option>
                  {LANGUAGE_OPTIONS.map(lang => (
                    <option key={lang.value} value={lang.value}>{lang.label}</option>
                  ))}
                </select>
                {(data.languages || []).length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeLanguage(index)}
                    className="flex items-center justify-center w-8 h-8 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-lg hover:from-red-600 hover:to-red-700 transition-all duration-200 shadow-sm hover:shadow-md"
                    title="删除此语言"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* 旅行历史 */}
        <div className="bg-white p-6 rounded-lg shadow-sm mb-6">
          <div className="mb-4">
            <h4 className="text-lg font-semibold text-gray-800 flex items-center mb-4">
              <Globe className="w-5 h-5 mr-2" />
              您是否在最近五年去过其他国家和地区旅行？
            </h4>
            <p className="text-xs text-amber-600 mb-2">提示：过去5年去过的所有国家/地区都要如实填写，包括：港澳台、日本、韩国、东南亚、欧洲等；只转机但实际入境的也建议写上。</p>
          </div>

          <div className="mb-4">
            
            <div className="flex gap-6">
              <label className="flex items-center cursor-pointer p-3 border-2 border-gray-200 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-all duration-200">
                <input
                  type="radio"
                  name="countries_visited_ind"
                  value="false"
                  checked={data.countries_visited_ind !== true}
                  onChange={(e) => {
                    onChange('countries_visited_ind', false);
                    onChange('countries_visited', []);
                  }}
                  className="mr-3 w-5 h-5 text-blue-600 border-gray-300 focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50"
                />
                <span className="text-base font-medium text-gray-700">❌ 否 - 最近五年没有去过其他国家</span>
              </label>
              
              <label className="flex items-center cursor-pointer p-3 border-2 border-gray-200 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-all duration-200">
                <input
                  type="radio"
                  name="countries_visited_ind"
                  value="true"
                  checked={data.countries_visited_ind === true}
                  onChange={(e) => {
                    onChange('countries_visited_ind', true);
                    if (!data.countries_visited || data.countries_visited.length === 0) {
                      addCountryVisited();
                    }
                  }}
                  className="mr-3 w-5 h-5 text-blue-600 border-gray-300 focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50"
                />
                <span className="text-base font-medium text-gray-700">✅ 是 - 最近五年去过其他国家</span>
              </label>
            </div>
          </div>

          {data.countries_visited_ind && (
            <div className="space-y-3">
              {birthCountry && (
                <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                  <div className="flex items-start">
                    <MapPin className="w-4 h-4 text-blue-600 mt-0.5 mr-2 flex-shrink-0" />
                    <p className="text-sm text-blue-800">
                      <span className="font-semibold">提示：</span> 您的出生国家（{getCountryName(birthCountry)}）已自动排除在访问国家列表之外。
                    </p>
                  </div>
                </div>
              )}
              <div className="flex justify-end mb-2">
                <button
                  type="button"
                  onClick={addCountryVisited}
                  className="flex items-center px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all duration-200 text-sm font-medium shadow-sm hover:shadow-md"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  添加国家
                </button>
              </div>
              
              {(data.countries_visited || []).map((country, index) => (
                <div key={index} className="flex items-center gap-3">
                  <CountrySelect
                    label="访问过的国家和地区"
                    value={country.country_code || ''}
                    onChange={(value) => updateCountryVisited(index, value)}
                    className="flex-1"
                    excludeCountries={birthCountry ? [birthCountry] : []}
                    required
                  />
                  {(data.countries_visited || []).length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeCountryVisited(index)}
                      className="flex items-center justify-center w-8 h-8 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-lg hover:from-red-600 hover:to-red-700 transition-all duration-200 shadow-sm hover:shadow-md"
                      title="删除此国家"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* 组织成员 */}
        <div className="bg-white p-6 rounded-lg shadow-sm mb-6">
          <div className="mb-4">
            <h4 className="text-lg font-semibold text-gray-800 flex items-center mb-4">
              <Building2 className="w-5 h-5 mr-2" />
              您是否属于、工作或贡献过专业、社会或慈善组织？
            </h4>
          </div>

          <div className="mb-4">

            <div className="flex gap-6">
              <label className="flex items-center cursor-pointer p-3 border-2 border-gray-200 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-all duration-200">
                <input
                  type="radio"
                  name="organization_ind"
                  value="false"
                  checked={data.organization_ind !== true}
                  onChange={(e) => {
                    onChange('organization_ind', false);
                    onChange('organizations', []);
                  }}
                  className="mr-3 w-5 h-5 text-blue-600 border-gray-300 focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50"
                />
                <span className="text-base font-medium text-gray-700">❌ 否 - 我没有参与过此类组织</span>
              </label>

              <label className="flex items-center cursor-pointer p-3 border-2 border-gray-200 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-all duration-200">
                <input
                  type="radio"
                  name="organization_ind"
                  value="true"
                  checked={data.organization_ind === true}
                  onChange={(e) => {
                    onChange('organization_ind', true);
                    if (!data.organizations || data.organizations.length === 0) {
                      addOrganization();
                    }
                  }}
                  className="mr-3 w-5 h-5 text-blue-600 border-gray-300 focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50"
                />
                <span className="text-base font-medium text-gray-700">✅ 是 - 我参与过专业、社会或慈善组织</span>
              </label>
            </div>
          </div>

          {data.organization_ind && (
            <div className="space-y-3">
              {/* 强烈风险警告 */}
              <div className="bg-red-50 border-2 border-red-400 rounded-lg p-4 animate-pulse">
                <div className="flex items-start">
                  <AlertTriangle className="w-6 h-6 text-red-600 mt-0.5 mr-3 flex-shrink-0" />
                  <div className="text-sm text-red-800">
                    <p className="font-bold text-base mb-2">⚠️ 重要风险警告</p>
                    <p className="mb-2">
                      <span className="font-bold">选择"是"将大幅增加拒签风险！</span>
                      此选项可能导致：
                    </p>
                    <ul className="list-disc list-inside ml-2 space-y-1">
                      <li className="font-semibold">面签时遭到严格审查</li>
                      <li className="font-semibold">签证申请被拒绝</li>
                      <li className="font-semibold">可能面临永久拒签</li>
                    </ul>
                    <p className="mt-3 font-bold text-red-900 bg-red-100 p-2 rounded">
                      💡 强烈建议：如无必要，请仔细核实后选择"否"。如确实存在相关情况，请立即咨询专业移民律师！
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex justify-end mb-2">
                <button
                  type="button"
                  onClick={addOrganization}
                  className="flex items-center px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all duration-200 text-sm font-medium shadow-sm hover:shadow-md"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  添加组织
                </button>
              </div>

              {(data.organizations || []).map((org, index) => (
                <div key={index} className="flex items-center gap-3">
                  <FormField
                    value={org.organization_name || ''}
                    onChange={(e) => updateOrganization(index, e.target.value)}
                    placeholder="请输入组织名称"
                    className="flex-1"
                    required
                  />
                  {(data.organizations || []).length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeOrganization(index)}
                      className="flex items-center justify-center w-8 h-8 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-lg hover:from-red-600 hover:to-red-700 transition-all duration-200 shadow-sm hover:shadow-md"
                      title="删除此组织"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* 特殊技能 */}
        <div className="bg-white p-6 rounded-lg shadow-sm mb-6">
          <div className="mb-4">
            <h4 className="text-lg font-semibold text-gray-800 flex items-center mb-4">
              <Award className="w-5 h-5 mr-2 text-indigo-600" />
              您是否有特殊技能或培训（如武器、爆炸物、核、生物或化学经验）？
            </h4>
          </div>
          
          <div className="mb-4">
            
            <div className="flex gap-6">
              <label className="flex items-center cursor-pointer p-3 border-2 border-gray-200 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-all duration-200">
                <input
                  type="radio"
                  name="specialized_skills_ind"
                  value="false"
                  checked={data.specialized_skills_ind !== true}
                  onChange={(e) => {
                    onChange('specialized_skills_ind', false);
                    onChange('specialized_skills_explanation', '');
                  }}
                  className="mr-3 w-5 h-5 text-blue-600 border-gray-300 focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50"
                />
                <span className="text-base font-medium text-gray-700">❌ 否 - 我没有特殊技能或培训</span>
              </label>
              
              <label className="flex items-center cursor-pointer p-3 border-2 border-gray-200 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-all duration-200">
                <input
                  type="radio"
                  name="specialized_skills_ind"
                  value="true"
                  checked={data.specialized_skills_ind === true}
                  onChange={(e) => onChange('specialized_skills_ind', true)}
                  className="mr-3 w-5 h-5 text-blue-600 border-gray-300 focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50"
                />
                <span className="text-base font-medium text-gray-700">✅ 是 - 我有特殊技能或培训</span>
              </label>
            </div>
          </div>

          {data.specialized_skills_ind && (
            <div className="mt-4 space-y-3">
              {/* 强烈风险警告 */}
              <div className="bg-red-50 border-2 border-red-400 rounded-lg p-4 animate-pulse">
                <div className="flex items-start">
                  <AlertTriangle className="w-6 h-6 text-red-600 mt-0.5 mr-3 flex-shrink-0" />
                  <div className="text-sm text-red-800">
                    <p className="font-bold text-base mb-2">⚠️ 重要风险警告</p>
                    <p className="mb-2">
                      <span className="font-bold">选择"是"将大幅增加拒签风险！</span>
                      此选项可能导致：
                    </p>
                    <ul className="list-disc list-inside ml-2 space-y-1">
                      <li className="font-semibold">面签时遭到严格审查</li>
                      <li className="font-semibold">签证申请被拒绝</li>
                      <li className="font-semibold">可能面临永久拒签</li>
                    </ul>
                    <p className="mt-3 font-bold text-red-900 bg-red-100 p-2 rounded">
                      💡 强烈建议：如无必要，请仔细核实后选择"否"。如确实存在相关情况，请立即咨询专业移民律师！
                    </p>
                  </div>
                </div>
              </div>

              <FormField
                label="请说明特殊技能或培训"
                type="textarea"
                value={data.specialized_skills_explanation || ''}
                onChange={(e) => onChange('specialized_skills_explanation', e.target.value)}
                placeholder="请详细说明您的特殊技能或培训..."
                required
              />
            </div>
          )}
        </div>

        {/* 军事服务 */}
        <div className="bg-white p-6 rounded-lg shadow-sm mb-6">
          <div className="mb-4">
            <h4 className="text-lg font-semibold text-gray-800 flex items-center mb-4">
              <Shield className="w-5 h-5 mr-2" />
              您是否曾在军队服役？
            </h4>
          </div>
          
          <div className="mb-4">
            
            <div className="flex gap-6">
              <label className="flex items-center cursor-pointer p-3 border-2 border-gray-200 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-all duration-200">
                <input
                  type="radio"
                  name="military_service_ind"
                  value="false"
                  checked={data.military_service_ind !== true}
                  onChange={(e) => {
                    onChange('military_service_ind', false);
                    onChange('military_services', []);
                  }}
                  className="mr-3 w-5 h-5 text-blue-600 border-gray-300 focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50"
                />
                <span className="text-base font-medium text-gray-700">❌ 否 - 我没有军队服役经历</span>
              </label>
              
              <label className="flex items-center cursor-pointer p-3 border-2 border-gray-200 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-all duration-200">
                <input
                  type="radio"
                  name="military_service_ind"
                  value="true"
                  checked={data.military_service_ind === true}
                  onChange={(e) => {
                    onChange('military_service_ind', true);
                    if (!data.military_services || data.military_services.length === 0) {
                      addMilitaryService();
                    }
                  }}
                  className="mr-3 w-5 h-5 text-blue-600 border-gray-300 focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50"
                />
                <span className="text-base font-medium text-gray-700">✅ 是 - 我曾在军队服役</span>
              </label>
            </div>
          </div>

          {data.military_service_ind && (
            <div className="space-y-6">
              {/* 强烈风险警告 */}
              <div className="bg-red-50 border-2 border-red-400 rounded-lg p-4 animate-pulse">
                <div className="flex items-start">
                  <AlertTriangle className="w-6 h-6 text-red-600 mt-0.5 mr-3 flex-shrink-0" />
                  <div className="text-sm text-red-800">
                    <p className="font-bold text-base mb-2">⚠️ 重要风险警告</p>
                    <p className="mb-2">
                      <span className="font-bold">选择"是"将大幅增加拒签风险！</span>
                      此选项可能导致：
                    </p>
                    <ul className="list-disc list-inside ml-2 space-y-1">
                      <li className="font-semibold">面签时遭到严格审查</li>
                      <li className="font-semibold">签证申请被拒绝</li>
                      <li className="font-semibold">可能面临永久拒签</li>
                    </ul>
                    <p className="mt-3 font-bold text-red-900 bg-red-100 p-2 rounded">
                      💡 强烈建议：如无必要，请仔细核实后选择"否"。如确实存在相关情况，请立即咨询专业移民律师！
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex justify-end mb-2">
                <button
                  type="button"
                  onClick={addMilitaryService}
                  className="flex items-center px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all duration-200 text-sm font-medium shadow-sm hover:shadow-md"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  添加服役记录
                </button>
              </div>

              {(data.military_services || []).map((service, index) => (
                <div key={index} className="p-6 bg-gradient-to-r from-gray-50 to-green-50 rounded-lg border-l-4 border-green-400 shadow-sm">
                  <div className="flex items-center justify-between mb-4">
                    <h5 className="text-lg font-semibold text-green-800">服役记录 {index + 1}</h5>
                    {(data.military_services || []).length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeMilitaryService(index)}
                        className="flex items-center justify-center w-8 h-8 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-lg hover:from-red-600 hover:to-red-700 transition-all duration-200 shadow-sm hover:shadow-md"
                        title="删除此服役记录"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>

                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <CountrySelect
                        label="服役国家"
                        value={service.country_code || ''}
                        onChange={(value) => updateMilitaryService(index, 'country_code', value)}
                        required
                      />
                      <FormField
                        label="军种"
                        value={service.branch || ''}
                        onChange={(e) => updateMilitaryService(index, 'branch', e.target.value)}
                        placeholder="陆军/海军/空军等"
                        required
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <FormField
                        label="军衔"
                        value={service.rank || ''}
                        onChange={(e) => updateMilitaryService(index, 'rank', e.target.value)}
                        placeholder="上尉"
                        required
                      />
                      <FormField
                        label="专业/职务"
                        value={service.specialty || ''}
                        onChange={(e) => updateMilitaryService(index, 'specialty', e.target.value)}
                        placeholder="通信/后勤等"
                        required
                      />
                    </div>

                    {/* 服役日期 */}
                    <div className="space-y-4">
                      <h6 className="text-md font-semibold text-gray-700">服役日期</h6>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* 开始日期 */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">开始日期</label>
                          <div className="grid grid-cols-3 gap-2">
                            <input
                              type="number"
                              min="1950"
                              max="2030"
                              placeholder="2010"
                              value={service.date_from?.year || ''}
                              onChange={(e) => updateMilitaryServiceDate(index, 'date_from', 'year', e.target.value)}
                              className="w-full h-10 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent text-center bg-gradient-to-r from-white to-green-50 shadow-sm hover:shadow-md transition-all duration-200 font-medium text-sm"
                            />
                            <select
                              value={service.date_from?.month || ''}
                              onChange={(e) => updateMilitaryServiceDate(index, 'date_from', 'month', e.target.value)}
                              className="w-full h-10 px-3 py-2 pr-8 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent bg-gradient-to-r from-white to-green-50 shadow-sm hover:shadow-md transition-all duration-200 appearance-none cursor-pointer font-medium text-sm"
                            >
                              <option value="">月</option>
                              {MONTHS.map(month => (
                                <option key={month.value} value={month.value}>{month.label.split(' ')[0]}</option>
                              ))}
                            </select>
                            <select
                              value={service.date_from?.day || ''}
                              onChange={(e) => updateMilitaryServiceDate(index, 'date_from', 'day', e.target.value)}
                              disabled={!service.date_from?.year || !service.date_from?.month}
                              className="w-full h-10 px-3 py-2 pr-8 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent bg-gradient-to-r from-white to-green-50 shadow-sm hover:shadow-md transition-all duration-200 appearance-none cursor-pointer font-medium text-sm disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
                            >
                              <option value="">日</option>
                              {generateDayOptions(service.date_from?.year, service.date_from?.month).map(day => (
                                <option key={day.value} value={day.value}>{day.label}</option>
                              ))}
                            </select>
                          </div>
                        </div>

                        {/* 结束日期 */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">结束日期</label>
                          <div className="grid grid-cols-3 gap-2">
                            <input
                              type="number"
                              min="1950"
                              max="2030"
                              placeholder="2015"
                              value={service.date_to?.year || ''}
                              onChange={(e) => updateMilitaryServiceDate(index, 'date_to', 'year', e.target.value)}
                              className="w-full h-10 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent text-center bg-gradient-to-r from-white to-green-50 shadow-sm hover:shadow-md transition-all duration-200 font-medium text-sm"
                            />
                            <select
                              value={service.date_to?.month || ''}
                              onChange={(e) => updateMilitaryServiceDate(index, 'date_to', 'month', e.target.value)}
                              className="w-full h-10 px-3 py-2 pr-8 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent bg-gradient-to-r from-white to-green-50 shadow-sm hover:shadow-md transition-all duration-200 appearance-none cursor-pointer font-medium text-sm"
                            >
                              <option value="">月</option>
                              {MONTHS.map(month => (
                                <option key={month.value} value={month.value}>{month.label.split(' ')[0]}</option>
                              ))}
                            </select>
                            <select
                              value={service.date_to?.day || ''}
                              onChange={(e) => updateMilitaryServiceDate(index, 'date_to', 'day', e.target.value)}
                              disabled={!service.date_to?.year || !service.date_to?.month}
                              className="w-full h-10 px-3 py-2 pr-8 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent bg-gradient-to-r from-white to-green-50 shadow-sm hover:shadow-md transition-all duration-200 appearance-none cursor-pointer font-medium text-sm disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
                            >
                              <option value="">日</option>
                              {generateDayOptions(service.date_to?.year, service.date_to?.month).map(day => (
                                <option key={day.value} value={day.value}>{day.label}</option>
                              ))}
                            </select>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* 敏感背景问题 */}
        <div className="bg-white p-6 rounded-lg shadow-sm mb-6">
          <div className="flex items-center mb-4">
            <AlertTriangle className="w-5 h-5 mr-2 text-red-600" />
            <h4 className="text-lg font-semibold text-gray-800">敏感背景信息</h4>
          </div>

          {/* 塔利班相关 */}
          <div className="mb-4">
            <h5 className="text-md font-semibold text-gray-800 mb-3">您是否曾为塔利班成员？</h5>
            <div className="flex gap-6 mb-3">
              <label className="flex items-center cursor-pointer p-3 border-2 border-gray-200 rounded-lg hover:border-red-400 hover:bg-red-50 transition-all duration-200">
                <input
                  type="radio"
                  name="taliban_ind"
                  value="false"
                  checked={data.taliban_ind !== true}
                  onChange={(e) => {
                    onChange('taliban_ind', false);
                    onChange('taliban_explanation', '');
                  }}
                  className="mr-3 w-5 h-5 text-red-600 border-gray-300 focus:ring-2 focus:ring-red-500 focus:ring-opacity-50"
                />
                <span className="text-base font-medium text-gray-700">❌ 否 - 我从未为塔利班成员</span>
              </label>
              
              <label className="flex items-center cursor-pointer p-3 border-2 border-gray-200 rounded-lg hover:border-red-400 hover:bg-red-50 transition-all duration-200">
                <input
                  type="radio"
                  name="taliban_ind"
                  value="true"
                  checked={data.taliban_ind === true}
                  onChange={(e) => onChange('taliban_ind', true)}
                  className="mr-3 w-5 h-5 text-red-600 border-gray-300 focus:ring-2 focus:ring-red-500 focus:ring-opacity-50"
                />
                <span className="text-base font-medium text-gray-700">✅ 是 - 我曾为塔利班成员</span>
              </label>
            </div>

            {data.taliban_ind && (
              <div className="mt-4 space-y-3">
                {/* 强烈风险警告 */}
                <div className="bg-red-50 border-2 border-red-400 rounded-lg p-4 animate-pulse">
                  <div className="flex items-start">
                    <AlertTriangle className="w-6 h-6 text-red-600 mt-0.5 mr-3 flex-shrink-0" />
                    <div className="text-sm text-red-800">
                      <p className="font-bold text-base mb-2">⚠️ 重要风险警告</p>
                      <p className="mb-2">
                        <span className="font-bold">选择"是"将大幅增加拒签风险！</span>
                        此选项可能导致：
                      </p>
                      <ul className="list-disc list-inside ml-2 space-y-1">
                        <li className="font-semibold">面签时遭到严格审查</li>
                        <li className="font-semibold">签证申请被拒绝</li>
                        <li className="font-semibold">可能面临永久拒签</li>
                      </ul>
                      <p className="mt-3 font-bold text-red-900 bg-red-100 p-2 rounded">
                        💡 强烈建议：如无必要，请仔细核实后选择"否"。如确实存在相关情况，请立即咨询专业移民律师！
                      </p>
                    </div>
                  </div>
                </div>

                <FormField
                  label="请详细说明"
                  type="textarea"
                  value={data.taliban_explanation || ''}
                  onChange={(e) => onChange('taliban_explanation', e.target.value)}
                  placeholder="请详细说明与塔利班的关系..."
                  required
                />
              </div>
            )}
          </div>

          {/* 准军事组织相关 */}
          <div>
            <h5 className="text-md font-semibold text-gray-800 mb-3">您是否曾参与准军事、暴动、游击或叛乱组织？</h5>
            <div className="flex gap-6 mb-3">
              <label className="flex items-center cursor-pointer p-3 border-2 border-gray-200 rounded-lg hover:border-red-400 hover:bg-red-50 transition-all duration-200">
                <input
                  type="radio"
                  name="insurgent_org_ind"
                  value="false"
                  checked={data.insurgent_org_ind !== true}
                  onChange={(e) => {
                    onChange('insurgent_org_ind', false);
                    onChange('insurgent_org_explanation', '');
                  }}
                  className="mr-3 w-5 h-5 text-red-600 border-gray-300 focus:ring-2 focus:ring-red-500 focus:ring-opacity-50"
                />
                <span className="text-base font-medium text-gray-700">❌ 否 - 我从未参与此类组织</span>
              </label>
              
              <label className="flex items-center cursor-pointer p-3 border-2 border-gray-200 rounded-lg hover:border-red-400 hover:bg-red-50 transition-all duration-200">
                <input
                  type="radio"
                  name="insurgent_org_ind"
                  value="true"
                  checked={data.insurgent_org_ind === true}
                  onChange={(e) => onChange('insurgent_org_ind', true)}
                  className="mr-3 w-5 h-5 text-red-600 border-gray-300 focus:ring-2 focus:ring-red-500 focus:ring-opacity-50"
                />
                <span className="text-base font-medium text-gray-700">✅ 是 - 我曾参与此类组织</span>
              </label>
            </div>

            {data.insurgent_org_ind && (
              <div className="mt-4 space-y-3">
                {/* 强烈风险警告 */}
                <div className="bg-red-50 border-2 border-red-400 rounded-lg p-4 animate-pulse">
                  <div className="flex items-start">
                    <AlertTriangle className="w-6 h-6 text-red-600 mt-0.5 mr-3 flex-shrink-0" />
                    <div className="text-sm text-red-800">
                      <p className="font-bold text-base mb-2">⚠️ 重要风险警告</p>
                      <p className="mb-2">
                        <span className="font-bold">选择"是"将大幅增加拒签风险！</span>
                        此选项可能导致：
                      </p>
                      <ul className="list-disc list-inside ml-2 space-y-1">
                        <li className="font-semibold">面签时遭到严格审查</li>
                        <li className="font-semibold">签证申请被拒绝</li>
                        <li className="font-semibold">可能面临永久拒签</li>
                      </ul>
                      <p className="mt-3 font-bold text-red-900 bg-red-100 p-2 rounded">
                        💡 强烈建议：如无必要，请仔细核实后选择"否"。如确实存在相关情况，请立即咨询专业移民律师！
                      </p>
                    </div>
                  </div>
                </div>

                <FormField
                  label="请详细说明"
                  type="textarea"
                  value={data.insurgent_org_explanation || ''}
                  onChange={(e) => onChange('insurgent_org_explanation', e.target.value)}
                  placeholder="请详细说明参与的组织情况..."
                  required
                />
              </div>
            )}
          </div>
        </div>

        {/* 预览信息 */}
        <div className="mt-6 p-4 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg border-l-4 border-indigo-400 shadow-sm">
          <h4 className="font-semibold text-indigo-800 mb-2">信息预览</h4>
          <div className="text-sm text-indigo-700 space-y-2">
            <p><strong>部落/宗族:</strong> {data.clan_tribe_ind ? '是' : '否'}</p>
            <p><strong>语言:</strong> {(data.languages || []).map(l => LANGUAGE_OPTIONS.find(opt => opt.value === l.language_name)?.label || l.language_name).join(', ') || '未填写'}</p>
            <p><strong>访问过的国家和地区:</strong> {data.countries_visited_ind ? `${(data.countries_visited || []).length} 个国家和地区` : '无'}</p>
            <p><strong>组织成员:</strong> {data.organization_ind ? `${(data.organizations || []).length} 个组织` : '无'}</p>
            <p><strong>特殊技能:</strong> {data.specialized_skills_ind ? '有' : '无'}</p>
            <p><strong>军事服务:</strong> {data.military_service_ind ? `${(data.military_services || []).length} 条记录` : '无'}</p>
            <p><strong>塔利班:</strong> {data.taliban_ind ? '是' : '否'}</p>
            <p><strong>准军事组织:</strong> {data.insurgent_org_ind ? '是' : '否'}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WorkEducation3Form; 
